import { Component, computed, inject } from '@angular/core';
import { NgClass } from '@angular/common';
import { RouterLink, Router, NavigationEnd } from '@angular/router';
import { AngularSvgIconModule } from 'angular-svg-icon';
import { toSignal } from '@angular/core/rxjs-interop';
import { filter, map } from 'rxjs';
import { LayoutResponsiveService } from '../layout-responsive.service';
import { PermissionService } from '../../../services/permission.service';
import { AgencyPermission, USER_ACCOUNT_PERMISSIONS } from '../../../shared/utils/agency-rbac';
import { AgencyUsersService } from '../../../services/agency-users.service';
import { NavItem } from '../../../models/navItem';

/** Même type que la sidebar desktop : NavItem + permission requise optionnelle. */
type GatedNavItem = NavItem & { permission?: AgencyPermission | AgencyPermission[] };

/**
 * Lot I (client clinique — item 21) : drawer mobile de la sidebar (< 1024px).
 * Réutilise exactement les mêmes items/routeClick que AgencySidebarComponent ;
 * visibilité pilotée par CSS (media queries) pour ne rien changer au desktop.
 */
@Component({
  selector: 'app-agency-sidebar-mobile',
  templateUrl: './agency-sidebar-mobile.component.html',
  styleUrl: './agency-sidebar-mobile.component.scss',
  imports: [NgClass, RouterLink, AngularSvgIconModule],
  standalone: true
})
export class AgencySidebarMobileComponent {
  private readonly layout = inject(LayoutResponsiveService);
  private readonly router = inject(Router);
  private readonly permissions = inject(PermissionService);
  private readonly usersService = inject(AgencyUsersService);

  /** Comptes pro en attente de validation (badge « Comptes professionnels »). */
  readonly pendingProCount = toSignal(this.usersService.pendingProCount$, { initialValue: 0 });

  /** Miroir de la liste d'items de la sidebar desktop (filtre par permission).
      TASK-05 : mêmes permissions que la desktop — sans elles, les liens gated
      (collaborateurs, affectation directe, relevés d'heures, audit) s'affichaient
      pour tous les comptes sur mobile avant le refus du permissionGuard. */
  private readonly allNavItems: GatedNavItem[] = [
    { id: 1, label: 'Gestion des établissements', icon: 'company', route: '/agency/establishment' },
    { id: 2, label: 'Gestion des professionnels de santé', icon: 'doctor', route: 'agency/candidates' },
    { id: 4, label: 'Gestion des collaborateurs', icon: 'padlock-tools', route: 'agency/users', permission: USER_ACCOUNT_PERMISSIONS },
    { id: 11, label: 'Comptes professionnels', icon: 'doctor', route: 'agency/pro-accounts', permission: AgencyPermission.VALIDATE_PRO_ACCOUNT },
    { id: 7, label: 'Affectation directe', icon: 'target', route: '/agency/direct-assign', permission: AgencyPermission.MISSION_WRITE },
    { id: 8, label: "Relevés d'heures", icon: 'document', route: '/agency/weekly-hours', permission: AgencyPermission.FINANCE_VIEW },
    { id: 10, label: "Journal d'audit", icon: 'document', route: '/agency/audit', permission: AgencyPermission.AUDIT_VIEW }
  ];

  private readonly url = toSignal(
    this.router.events.pipe(
      filter((e): e is NavigationEnd => e instanceof NavigationEnd),
      map(e => e.urlAfterRedirects)
    ),
    { initialValue: this.router.url }
  );

  routeSelected = computed(() => {
    const current = this.url();
    return this.allNavItems.find(item =>
      item.route && current.includes(item.route)
    )?.id;
  });

  readonly isOpen = computed(() => this.layout.sidebarOpen());

  get navList() {
    // Même sémantique que la desktop : filtre par permission (les items sans
    // permission requise passent toujours).
    return this.allNavItems.filter((item: any) => this.permissions.has(item.permission));
  }

  routeClick(item: any): void {
    if (this.permissions.has(AgencyPermission.VALIDATE_PRO_ACCOUNT)) {
      this.usersService.refreshPendingProCount();
    }
    this.router.navigate([item.route]);
    this.layout.close();
  }

  onBackdropClick(): void {
    this.layout.close();
  }
}
